import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Chat as ChatIcon,
} from '@mui/icons-material';
import {
  collection,
  query,
  where,
  getDocs,
  addDoc,
  updateDoc,
  doc,
} from 'firebase/firestore';
import { db } from '../firebaseConfig';
import TicketList from '../components/tickets/TicketList';
import TicketChat from '../components/tickets/TicketChat';
import { useOutletContext } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { createTicket, setTickets } from '../redux/slices/ticketsSlice';
import nodeAxiosFirebase from '../utils/nodeAxiosFirebase';
import { useTranslation } from 'react-i18next';

const TicketsPage = () => {
  const { openTicketDialog, setOpenTicketDialog } = useOutletContext();
  const dispatch = useDispatch();
  const { tickets } = useSelector((state) => state.tickets);
  const { customers } = useSelector((state) => state.customers);
  const { query: searchTerm } = useSelector((state) => state.search);
  const { t } = useTranslation();
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [openChat, setOpenChat] = useState(false);
  const [loading, setLoading] = useState(false);
  const [structures, setStructures] = useState([]);
  const [modules, setModules] = useState([]);
  const [users, setUsers] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    priority: 'medium',
    status: 'open',
    accountId: '',
    structureId: '',
    moduleId: '',
    tags: [],
    assignedTo: '',
  });

  const priorities = ['low', 'medium', 'high', 'critical'];
  const statuses = ['open', 'in_progress', 'resolved', 'closed'];

  useEffect(() => {
    fetchTickets();
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const usersRef = collection(db, 'users');
      const q = query(usersRef);
      const querySnapshot = await getDocs(q);
      const usersData = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setUsers(usersData);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const fetchTickets = async () => {
    try {
      const ticketsRef = collection(db, 'tickets');
      const q = query(ticketsRef);
      const querySnapshot = await getDocs(q);
      const ticketsData = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      dispatch(setTickets(ticketsData));
    } catch (error) {
      console.error('Error fetching tickets:', error);
    }
  };

  const fetchStructuresAndModules = async (accountId) => {
    if (!accountId) return;

    try {
      setLoading(true);
      const response = await nodeAxiosFirebase({
        method: 'POST',
        url: 'master',
        body: {
          businessId: accountId,
          action: 'details',
        },
      });

      if (response) {
        setStructures(response.structures || []);
        setModules(response.modules || []);
      }
    } catch (error) {
      console.error('Error fetching structures and modules:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAccountChange = (accountId) => {
    setFormData((prev) => ({
      ...prev,
      accountId,
      structureId: '',
      moduleId: '',
    }));
    fetchStructuresAndModules(accountId);
  };

  const handleCreateTicket = async () => {
    try {
      await dispatch(createTicket(formData));
      setOpenTicketDialog(false);
      resetForm();
    } catch (error) {
      console.error('Error creating ticket:', error);
    }
  };

  const handleUpdateTicket = async () => {
    try {
      const ticketRef = doc(db, 'tickets', selectedTicket.id);
      await updateDoc(ticketRef, {
        ...formData,
        updatedAt: new Date(),
      });
      setOpenTicketDialog(false);
      fetchTickets();
      resetForm();
    } catch (error) {
      console.error('Error updating ticket:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      priority: 'medium',
      status: 'open',
      accountId: '',
      structureId: '',
      moduleId: '',
      tags: [],
      assignedTo: '',
    });
    setSelectedTicket(null);
    setStructures([]);
    setModules([]);
  };

  const handleOpenDialog = (ticket = null) => {
    if (ticket) {
      setSelectedTicket(ticket);
      setFormData(ticket);
      if (ticket.accountId) {
        fetchStructuresAndModules(ticket.accountId);
      }
    }
    setOpenTicketDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenTicketDialog(false);
    resetForm();
  };

  return (
    <Box>
      <TicketList
        tickets={tickets}
        onEdit={handleOpenDialog}
        accounts={customers}
        users={users}
        searchTerm={searchTerm}
      />

      <Dialog
        open={openTicketDialog}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {selectedTicket ? 'Edit Ticket' : 'Create New Ticket'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} width={'100%'}>
              <TextField
                fullWidth
                label="Name"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={12} width={'100%'}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Description"
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={6} width={'50%'}>
              <FormControl fullWidth>
                <InputLabel>Priority</InputLabel>
                <Select
                  value={formData.priority}
                  label="Priority"
                  onChange={(e) =>
                    setFormData({ ...formData, priority: e.target.value })
                  }
                >
                  {priorities.map((priority) => (
                    <MenuItem key={priority} value={priority}>
                      {priority.charAt(0).toUpperCase() + priority.slice(1)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} width={'48%'}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={formData.status}
                  label="Status"
                  onChange={(e) =>
                    setFormData({ ...formData, status: e.target.value })
                  }
                >
                  {statuses.map((status) => (
                    <MenuItem key={status} value={status}>
                      {status
                        .split('_')
                        .map(
                          (word) => word.charAt(0).toUpperCase() + word.slice(1)
                        )
                        .join(' ')}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} width={'100%'}>
              <FormControl fullWidth>
                <InputLabel>Account</InputLabel>
                <Select
                  value={formData.accountId}
                  label="Account"
                  onChange={(e) => handleAccountChange(e.target.value)}
                >
                  {customers.map((customer) => (
                    <MenuItem key={customer.id} value={customer.id}>
                      {customer.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} width={'50%'}>
              <FormControl fullWidth>
                <InputLabel>Structure</InputLabel>
                <Select
                  value={formData.structureId}
                  label="Structure"
                  onChange={(e) =>
                    setFormData({ ...formData, structureId: e.target.value })
                  }
                  disabled={!formData.accountId || loading}
                >
                  {structures.map((structure) => (
                    <MenuItem key={structure.id} value={structure.id}>
                      {structure.name_fr ||
                        structure.name_en ||
                        structure.name ||
                        'Unnamed Structure'}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} width={'48%'}>
              <FormControl fullWidth>
                <InputLabel>Module</InputLabel>
                <Select
                  value={formData.moduleId}
                  label="Module"
                  onChange={(e) =>
                    setFormData({ ...formData, moduleId: e.target.value })
                  }
                  disabled={!formData.accountId || loading}
                >
                  {modules.map((module) => (
                    <MenuItem key={module.id} value={module.id}>
                      {module.name_fr ||
                        module.name_en ||
                        module.name ||
                        'Unnamed Module'}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} width={'100%'}>
              <FormControl fullWidth>
                <InputLabel>Assigned To</InputLabel>
                <Select
                  value={formData.assignedTo}
                  label="Assigned To"
                  onChange={(e) =>
                    setFormData({ ...formData, assignedTo: e.target.value })
                  }
                >
                  <MenuItem value="">Unassigned</MenuItem>
                  {users.map((user) => (
                    <MenuItem key={user.id} value={user.id}>
                      {user.displayName || user.email}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} width={'100%'}>
              <TextField
                fullWidth
                label="Tags"
                value={formData.tags.join(', ')}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    tags: e.target.value.split(',').map((tag) => tag.trim()),
                  })
                }
                helperText="Separate tags with commas"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={selectedTicket ? handleUpdateTicket : handleCreateTicket}
            variant="contained"
          >
            {selectedTicket ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TicketsPage;
